import { MyIcon } from '~/components/ui/MyIcon'
import { MyImage } from '~/components/ui/MyImage'
import { usePostSocial } from '~/hooks/usePostSocial'
import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { digitalUnit } from '~/utils'
import Link from 'next/link'
import {
  PostTypes,
  SquarePostScope,
  SquarePostType,
  SquarePostStatus,
  SuperType,
  AiArtworkServiceTypeGraphQLEnum,
  PostImageSourceType,
  SpecialImageTagEnum,
} from '~/graphqls/types'
import { MyAvatar } from '../../ui/MyAvatar'
import styles from './index.module.css'
import CoverDom from '../CoverDom'
import { useStores } from '~/hooks/useStores'
import { CDNImage } from '../CDNImage'
import { WhiteUserFrontControl } from '~/components/pages/plaza/WhiteUserFrontControl'
import AgainButton from './AgainButton'

const TEXT_LIMIT = 134

export const SinglePost = ({
  post,
  onActionComplete,
  showSticky,
  showProcessingTag = false,
  showAiGenTag = true,
}: {
  post: PostTypes & {
    competitionStyle?: number | null
  }
  onActionComplete?: (params?: { type?: string; id?: any }) => void
  showSticky?: boolean // 显示置顶
  showProcessingTag?: boolean // 显示待审核标签
  showAiGenTag?: boolean // 显示AI生成标签
}) => {
  const [isFocus, setIsFocus] = useState(false)

  const { liked, likeCount, handleLike, faved, handleFav } = usePostSocial(
    post,
    {
      onActionComplete,
    }
  )

  const { store } = useStores()
  const isSelf = store.userinfo?.base?.uid === post.user?.uid
  // 合集
  const isCollection = post.type === SquarePostType.AI_COLLECTION
  // 笔记
  const isNote = post.type === SquarePostType.NOTE
  // AI创作
  const isAiArtwork = post.type === SquarePostType.AI_ARTWORK
  // 精绘
  const isPrefine = post.aiArtworkInfo?.superType === SuperType.PREFINE
  // 4k
  const is4k = post.aiArtworkInfo?.superType === SuperType.FOUR_K
  // 纯文本
  const onlyText = !post?.coverImage?.imageUrl
  // 是否属于内部
  const isInSide = post.postScope === SquarePostScope.INSIDE
  // 显示审核状态
  const showProcessing =
    isSelf && showProcessingTag && post?.status === SquarePostStatus.WAIT
  // 显示专业版
  const isPro =
    post.imageList?.[0]?.aiArtwork?.serviceType ===
    AiArtworkServiceTypeGraphQLEnum.PROFESSIONAL
  // ai创作 四宫格

  // 四宫格
  const { fourGridImages, isArtworkFour } = useMemo(() => {
    const fourGridImages = isAiArtwork
      ? post?.specialImage
          ?.filter(item => item?.tagEnum === SpecialImageTagEnum.GRID_IMAGE)
          .map(it => ({ miniArtWorkUrl: it?.image?.imageUrl }))
          .slice(0, 4)
      : []
    const isArtworkFour = fourGridImages?.length === 4
    return {
      fourGridImages: isArtworkFour ? fourGridImages : [],
      isArtworkFour,
    }
  }, [post, isAiArtwork])

  const percentage = useMemo(() => {
    if (isCollection || isArtworkFour) {
      return (300 / 220) * 100
    }
    if (!post?.coverImage?.width || !post?.coverImage.height) {
      return 100
    }
    return (
      (Number(post?.coverImage?.height) / Number(post?.coverImage?.width)) * 100
    )
  }, [post?.coverImage, isCollection])

  const url = useMemo(() => {
    // 1.合集
    if (isCollection) {
      return `/collection/${post.id}`
    }
    // 2.笔记
    if (isNote) {
      return `/note/${post.id}`
    }
    // 3.ai创作
    return `/plaza/${post.id}`
  }, [isCollection, isNote, post.id])

  return (
    <div
      className="w-full relative"
      onMouseEnter={() => setIsFocus(true)}
      onMouseLeave={() => setIsFocus(false)}
    >
      <div
        className={classNames(
          'w-full h-0 relative overflow-hidden rounded-2xl',
          styles.post,
          {
            [styles.onlyTextWrap]: onlyText,
          }
        )}
        style={{
          paddingBottom: `${percentage}%`,
        }}
      >
        <div className="absolute left-0 top-0 bottom-0 right-0 group">
          <Link href={url} className="h-full">
            {isCollection ? (
              <>
                <CoverDom
                  data={post?.aiCollection?.coverAiArtworks?.map(
                    (it: any) => it.miniArtWorkUrl!
                  )}
                />
                <div className="absolute bg-neutral-50/50 left-0 top-0 right-0 bottom-0 z-[1] flex justify-center items-center rounded-[4px] text-lg font-bold p-3 text-center group-hover:hidden">
                  <span
                    className="px-3 break-all"
                    style={{
                      color: '#000',
                      WebkitTextStroke: '5px transparent',
                      background: '#fff',
                      WebkitBackgroundClip: 'text',
                      padding: '0 4px',
                    }}
                  >
                    {post.title}
                  </span>
                </div>
              </>
            ) : isArtworkFour ? (
              <CoverDom data={fourGridImages.map(it => it.miniArtWorkUrl!)} />
            ) : onlyText ? (
              <div className={styles.onlyText}>
                <div
                  className={classNames(
                    'text-overflow-line-2',
                    styles.onlyTextTitle
                  )}
                >
                  {post?.title}
                </div>
                {!!post?.content && (
                  <div className={styles.onlyTextContent}>
                    {post?.content?.length > TEXT_LIMIT
                      ? (post?.content || '').substring(0, TEXT_LIMIT) + '...'
                      : post?.content}
                  </div>
                )}
              </div>
            ) : (
              <>
                {isNote &&
                post?.coverImage?.sourceType ===
                  PostImageSourceType.AI_LAB_SVD_GIF ? (
                  <img
                    src={post?.coverImage?.imageUrl || ''}
                    className="w-full h-full object-cover"
                    alt={post?.title || ''}
                  />
                ) : (
                  <MyImage
                    src={post?.coverImage?.imageUrl}
                    tag="v800"
                    className="w-full h-full"
                    alt={post?.title || ''}
                    dark
                  />
                )}
              </>
            )}

            <div
              className={classNames(
                'flex justify-start items-center absolute left-2 top-2 right-2',
                styles.tags
              )}
            >
              {showProcessing ? (
                <div className="p-1 rounded text-xs leading-[12px] text-neutral-800 transform scale-90 bg-[#4BC9F0]">
                  待审核
                </div>
              ) : (
                <>
                  {showSticky && (
                    <CDNImage
                      src="/images/icons/top-sticky.png"
                      className="w-6 h-6 rounded-full mr-2"
                      alt="top"
                    />
                  )}
                  {isPrefine && !isPro && (
                    <div
                      className="text-center text-xs leading-none text-neutral-900 font-medium px-2.5 py-2 rounded-full mr-2"
                      style={{
                        background: `linear-gradient(269.96deg, #7851EC -22.07%, #FFC56F 131.97%)`,
                      }}
                    >
                      精绘
                    </div>
                  )}
                  {is4k && !isPro && (
                    <div
                      className="text-center text-xs leading-none text-neutral-50 font-medium px-2.5 py-1.5 rounded-full mr-2 min-w-[44px]"
                      style={{
                        background: `linear-gradient(290deg, #F2B767 8.7%, #EEE9E2 105.76%)`,
                      }}
                    >
                      4K
                    </div>
                  )}
                </>
              )}
              {isPro && (
                <div className="text-xs leading-none text-neutral-900 font-medium px-2.5 py-2 rounded-full mr-2 bg-[var(--primary)]">
                  专业版
                </div>
              )}
              {/* 有名次的大赛作品 */}
              {!!post.competitionStyle && (
                <div className="flex justify-center items-center absolute top-0 right-0 min-w-[60px] font-semibold text-neutral-900 text-xs leading-none py-1.5 whitespace-nowrap bg-neutral-50/[0.56] rounded-[14px]">
                  <CDNImage
                    src="/images/icons/icon-vector.png"
                    className="w-2 block"
                    alt="vector"
                  />
                  <span className="px-[3px]">No.{post.competitionStyle}</span>
                  <CDNImage
                    src="/images/icons/icon-vector.png"
                    className="w-2 block rotate-180"
                    alt="vector"
                  />
                </div>
              )}
              {/* 参赛作品 */}
              {post.competitionStyle === 0 && (
                <div className="absolute top-0 right-0 font-semibold text-neutral-900 text-xs leading-none p-1.5 whitespace-nowrap bg-neutral-50/[0.56] rounded-[14px]">
                  参赛作品
                </div>
              )}
            </div>

            {/** 多图 */}
            {!isArtworkFour &&
              post?.imageList &&
              post?.imageList?.length > 1 && (
                <div
                  className={classNames(
                    'absolute w-6 h-6 rounded-full bg-neutral-50/50 left-2 bottom-2 flex items-center justify-center',
                    styles.imageMul
                  )}
                >
                  <MyIcon
                    icon="image-stack"
                    className="text-base text-neutral-600"
                  />
                </div>
              )}

            {/** 描述词 */}
            {!!post?.aiArtworkInfo?.prompt && (
              <div className={styles.description}>
                <div>{post?.aiArtworkInfo?.prompt}</div>
              </div>
            )}

            {/* 合集 */}
            {isCollection && !!post?.content && (
              <div className={styles.description}>
                <div>{post?.content}</div>
              </div>
            )}
          </Link>

          {showAiGenTag && (
            <CDNImage
              src="/images/common/ai_gen_tag.png"
              className={classNames(
                'w-[42px] object-cover absolute right-2 z-10 bottom-2'
              )}
            />
          )}

          {/** Hover后展示的内容 */}
          <div className={styles.operateWrap}>
            <div className="flex">
              <div className={styles.operate} onClick={handleLike} title="点赞">
                <MyIcon
                  icon={liked ? 'heart-filled' : 'heart'}
                  className={classNames('text-[20px]', [
                    liked ? 'text-error' : 'text-neutral-600',
                  ])}
                />
              </div>
            </div>
            <div className="flex">
              <div
                className={classNames(styles.operate, 'ml-2')}
                onClick={handleFav}
                title="收藏"
              >
                <MyIcon
                  icon={faved ? 'star-filled' : 'star'}
                  className={classNames('text-[20px]', [
                    faved ? 'text-[#FFD600]' : 'text-neutral-600',
                  ])}
                />
              </div>

              {isAiArtwork && (
                <WhiteUserFrontControl
                  post={post}
                  isSquare
                  className={classNames(
                    styles.operate,
                    'text-neutral-900 text-large ml-2'
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {!isAiArtwork && isInSide && (
        <div
          className={classNames('mt-2 relative', {
            'mt-0': onlyText,
          })}
        >
          {!onlyText && (
            <div className="text-sm text-neutral-500 line-clamp-1">
              <Link href={url}>{post.title}</Link>
            </div>
          )}
          <div
            className={classNames('flex mt-1 justify-between items-center', {
              'mt-0': onlyText,
            })}
          >
            <div className="flex items-center">
              <MyAvatar
                className="shrink-0"
                image={post?.user?.avatarUrl as string}
                size={16}
                hiddenIcon
              />
              <span className="text-neutral-400 text-xs max-w-[80%] line-clamp-1 ml-1">
                {post?.user?.nickname}
              </span>
            </div>
            <div
              className={classNames('text-xs flex items-center', [
                liked ? 'text-error' : 'text-neutral-400',
              ])}
            >
              <MyIcon
                className="text-sm mr-0.5"
                icon={liked ? 'heart-filled' : 'heart'}
              />
              <span>{digitalUnit(likeCount)}</span>
            </div>
          </div>
        </div>
      )}

      {/* 一件同款专用 */}
      {isAiArtwork && isInSide && (
        <div className={classNames('mt-2 relative')}>
          <div className="text-sm text-neutral-500 line-clamp-1">
            <Link href={url}>{post.title}</Link>
          </div>
          <div className="flex mt-1 justify-between items-center">
            <div className="flex items-center">
              <MyAvatar
                className="shrink-0"
                image={post?.user?.avatarUrl as string}
                size={16}
                hiddenIcon
              ></MyAvatar>
              <span className="text-neutral-400 text-xs max-w-[80%] line-clamp-1 ml-1">
                {post?.user?.nickname}
              </span>
              {!!post?.user?.companyAuthInfo?.id && (
                <CDNImage
                  src="/images/common/company.png"
                  className="h-4 ml-1"
                />
              )}
            </div>

            <AgainButton post={post} isFocus={isFocus} />
          </div>
        </div>
      )}
    </div>
  )
}
